<template>
  <b-card class="shadow">
    <b-alert v-if="!canAuthEmailPassword && !canAuthSingleSignOn" class="text-center my-4 mb-1" show variant="warning">
      {{ $t('auth.login.disabled') }}
    </b-alert>

    <div class="mb-2 text-center">
      <template v-if="challenge && challenge.ChallengeName === 'NEW_PASSWORD_REQUIRED'">
        <h3>{{ $t('auth.login.new-password-title') }}</h3>
      </template>
      <template v-else-if="showLoginForm">
        <h3>{{ $t('auth.login.title') }}</h3>
        <!-- eslint-disable-next-line vue-i18n/no-missing-keys -->
        <p v-if="$te('auth.login.description')">{{ $t('auth.login.description') }}</p>
      </template>
      <template v-else>
        <h3>{{ $t('auth.welcome.title') }}</h3>
        <!-- eslint-disable-next-line vue-i18n/no-missing-keys -->
        <p v-if="$te('auth.welcome.description')">{{ $t('auth.welcome.description') }}</p>
      </template>
    </div>

    <b-alert v-if="err" class="mb-4" show variant="danger">
      <template v-if="$findApolloError(err, 'extensions.code', 'EMAIL_DOMAIN_INVALID')">
        {{ $t('auth.errors.email-domain-invalid', { validDomains: err.extensions.domains.join(', ') }) }}
      </template>
      <template v-else>
        {{ (err.extensions ? err.extensions.message : null) || err.user_message || err.message || err }}
      </template>
    </b-alert>

    <template v-if="emailAddress && isExternalProvider">
      <div class="mb-4 text-center">
        <p class="mb-1">{{ $t('auth.single-sign-on.prompt') }}:</p>
        <template v-for="(provider, i) in identityProviders.filter(({ identifiers: ids }) => Array.isArray(ids) && ids.find(id => emailAddress.includes(id)))">
          <single-sign-on-button :provider="provider" :key="i"/>
        </template>
      </div>
    </template>
    <template v-else-if="challenge && challenge.ChallengeName === 'NEW_PASSWORD_REQUIRED'">
      <form method="POST" @submit.prevent="submitChallenge">
        <b-form-group>
          <b-input-group>
            <b-form-input autocomplete="email email-address" id="emailAddress" type="email" :placeholder="$t('auth.placeholders.emailAddress')"
              :disabled="Boolean(emailAddress)" required v-model="form.emailAddress"/>
            <b-input-group-append v-if="Boolean(emailAddress)">
              <b-btn class="email-change-btn text-dark" @click="clearEmailAddress" tabindex="-1" variant="transparent">
                <font-awesome-icon icon="pencil-alt"/>
              </b-btn>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <b-form-group>
          <b-form-input autocomplete="new-password" id="new-password" type="password"
            :placeholder="$t('auth.placeholders.passwordNew')" required v-model="form.password" />
        </b-form-group>
        <b-form-group>
          <b-form-input autocomplete="off" id="new-password-again" type="password"
            :placeholder="$t('auth.placeholders.passwordConfirmNew')" required v-model="form.passwordConfirm" />
        </b-form-group>
        <b-form-group>
          <b-btn class="w-100" :disabled="working" size="md" type="submit" variant="primary">
            <template v-if="working">{{ $t('ctas.continuing') }}</template>
            <template v-else>{{ $t('ctas.continue') }}</template>
          </b-btn>
        </b-form-group>
        <p v-if="canAuthEmailPassword" class="d-flex flex-row justify-content-center align-items-center mb-0">
          <router-link :to="{ name: 'LoginForgot', params: { sendCode: true }, query: $route.query }">
            {{ $t('auth.login.forgot-prompt') }}
          </router-link>
        </p>
      </form>
    </template>
    <template v-else>
      <template v-if="canAuthEmailPassword">
        <form v-if="showLoginForm" method="POST" @submit.prevent="submitLogin">
          <b-form-group>
            <b-input-group>
              <b-form-input autocomplete="email email-address" id="emailAddress" type="email" :placeholder="$t('auth.placeholders.emailAddress')"
                :disabled="Boolean(emailAddress)" required v-model="form.emailAddress"/>
              <b-input-group-append v-if="Boolean(emailAddress)">
                <b-btn class="email-change-btn text-dark" @click="clearEmailAddress" tabindex="-1" variant="transparent">
                  <font-awesome-icon icon="pencil-alt"/>
                </b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-form-group>
            <b-form-input autocomplete="current-password" id="password" type="password"
              :placeholder="$t('auth.placeholders.password')" required v-model="form.password" />
          </b-form-group>
          <b-form-group>
            <b-btn class="w-100" :disabled="working" size="md" type="submit" variant="primary">
              <template v-if="working">{{ $t('auth.ctas.logging-in') }}</template>
              <template v-else>{{ $t('auth.ctas.login') }}</template>
            </b-btn>
          </b-form-group>
          <p v-if="canAuthEmailPassword" class="d-flex flex-row justify-content-center align-items-center mb-0">
            <router-link :to="{ name: 'LoginForgot', query: $route.query }">
              {{ $t('auth.login.forgot-prompt') }}
            </router-link>
          </p>
        </form>
        <form v-else method="POST" @submit.prevent="checkAuthentication">
          <b-form-group>
            <b-form-input autocomplete="email email-address" id="emailAddress" type="email"
              :placeholder="$t('auth.placeholders.emailAddress')" required v-model="form.emailAddress"/>
          </b-form-group>
          <b-form-group>
            <b-btn class="w-100" :disabled="working" size="md" type="submit" variant="primary">
              <template v-if="working">{{ $t('ctas.continuing') }}</template>
              <template v-else>{{ $t('ctas.continue') }}</template>
            </b-btn>
          </b-form-group>
        </form>
      </template>

      <p v-if="canAuthSingleSignOn && canAuthEmailPassword" class="text-center text-uppercase text-muted my-4">
        {{ $t('auth.login.or-divider') }}
      </p>

      <div class="mb-4 text-center" v-if="canAuthSingleSignOn">
        <p v-if="!canAuthEmailPassword" class="mb-1">{{ $t('auth.single-sign-on.prompt') }}:</p>
        <template v-for="(provider, i) in identityProviders" v-if="provider.type !== 'COGNITO'">
          <single-sign-on-button :provider="provider" :key="i"/>
        </template>
      </div>
    </template>
  </b-card>
</template>

<script>
import _get from 'lodash/get';
import gql from 'graphql-tag';
import AuthLoginByEmailMutation from '@src/graphql/mutations/AuthLoginByEmail.gql';
import AuthRespondToChallengeMutation from '@src/graphql/mutations/AuthRespondToChallenge.gql';
import authMixin from '@src/mixins/auth';
import SingleSignOnButton from '@src/components/auth/SingleSignOnButton';
import { toDataURI } from '@src/lib/utils';
import { mapRoot } from '@src/mixins/root';

const SUPPORTED_CHALLENGES = [
  'NEW_PASSWORD_REQUIRED',
];

export default {
  props: {
    company: Object,
    emailAddress: {
      type: String,
      default: () => null,
    },
  },
  components: {
    SingleSignOnButton,
  },
  mixins: [
    authMixin,
  ],
  data: () => ({
    challenge: null,
    err: null,
    form: {},
    isExternalProvider: false,
    showLoginForm: false,
    working: false,
  }),
  created() {
    const { name, query: { next, ...query } } = this.$route;

    // If we're trying to redirect to the Dashboard, drop this redirect
    if (next === '/dashboard' || next === '/dashboard-im') {
      this.$router.replace({ name, query });
    }

    if (!this.isDemoSite && this.isLoggedIn) {
      // If we're just logged in, shouldn't have got here
      return this.afterAuthenticated();
    } else {
      this.form.emailAddress = this.emailAddress;
    }
  },
  mounted() {
    this.focusFirst();
    // window.alert(`${this.$route.query.redirect}`);
    // window.alert(`${this.redirectTo}`);
  },
  metaInfo() {
    return {
      title: this.showLoginForm ? this.$t('auth.login.title') : this.$t('auth.welcome.title'),
    };
  },
  computed: {
    ...mapRoot([ 'isLoggedIn', 'isDemoSite' ]),
    loginImage() {
      return _get(this.company, 'authBranding.loginImage.fileLocation', '/static/images/illustrations/1.svg');
    },
  },
  methods: {
    focusFirst() {
      const firstInput = this.$el.querySelector('input:not([disabled]):first-child');
      firstInput && firstInput.focus();
    },
    async checkAuthentication() {
      this.working = true;

      try {
        const { emailAddress } = this.form;
        this.$emit('setEmailAddress', emailAddress);

        const { data, errors } = await this.$apollo.mutate({
          mutation: gql`
            mutation checkAuthByEmail($emailAddress: String!) {
              checkAuthByEmail(emailAddress: $emailAddress)
            }
          `,
          variables: { emailAddress },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$log.error(err));
          throw errors.shift();
        }

        const { checkAuthByEmail: state } = data;
        const { query } = this.$route;

        switch (state) {
          case 'CONFIRMED':
          case 'FORCE_CHANGE_PASSWORD':
            this.err = null;
            this.form = { emailAddress };
            this.showLoginForm = true;
            this.working = false;
            this.$nextTick(() => this.focusFirst());
            break;
          case 'UNCONFIRMED':
            this.$router.push({ name: 'RegisterVerify', query });
            break;
          case 'RESET_REQUIRED':
            this.$router.push({ name: 'LoginForgot', query });
            break;
          case 'EXTERNAL_PROVIDER':
            this.err = null;
            this.isExternalProvider = true;
            this.working = false;
            break;
          case null:
            this.$router.push({ name: 'Register', query });
            break;

          case 'ARCHIVED':
            this.$assert.fail(this.$t('auth.errors.account-archived-state'), { state });
            break;
          case 'UNKNOWN':
            this.$assert.fail(this.$t('auth.errors.account-unknown-state'), { state });
            break;
          case 'DISABLED':
            this.$assert.fail(this.$t('auth.errors.account-disabled-state'), { state });
            break;
          case 'COMPROMISED':
            this.$assert.fail(this.$t('auth.errors.account-compromised-state'), { state });
            break;

          default: {
            this.$log.error(`Unknown state ${state} returned from checkAuthByEmail`);
            throw new Error(this.$t('general.errored-try-later'));
          }
        }
      } catch (err) {
        this.err = err;
        this.working = false;
      }
    },
    clearEmailAddress() {
      this.err = null;
      this.isExternalProvider = false;
      this.showLoginForm = false;
      this.$emit('setEmailAddress', null);
      this.$nextTick(() => this.focusFirst());
    },
    async submitLogin() {
      try {
        this.working = true;
        const { emailAddress } = this.form;
        this.$emit('setEmailAddress', emailAddress);

        const { errors } = await this.$apollo.mutate({
          mutation: AuthLoginByEmailMutation,
          variables: {
            login: {
              emailAddress,
              password: this.form.password ? toDataURI('text/plain;charset=utf8', this.form.password) : null,
              contextData: this.getCognitoContextData(emailAddress),
            },
          },
          update: (client, { data: { loginByEmail } }) => this.updateAuthResponse(client, loginByEmail),
          errorPolicy: 'all',
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$log.error(err));

          const { extensions } = errors.find(err => err.extensions && err.extensions.ChallengeName) || {};
          if (extensions && extensions.ChallengeName) {
            return this.prepareChallenge(extensions);
          }
        }

        this.$assert(this.isLoggedIn, new Error('Failed to login by email'));
        await this.afterAuthenticated(this.nextRoute);
      } catch (err) {
        this.err = err;
        this.working = false;
      }
    },
    prepareChallenge({ ChallengeName, ChallengeParameters, Session }) {
      this.$assert(SUPPORTED_CHALLENGES.includes(ChallengeName), new Error(`Challenge "${ChallengeName}" is not supported`));

      this.challenge = { ChallengeName, ChallengeParameters, Session };
      this.form = { emailAddress: this.emailAddress };
      this.working = false;

      this.$nextTick(() => {
        const firstInput = this.$el.querySelector('input:not([disabled]):first-child');
        firstInput && firstInput.focus();
      });
    },
    async submitChallenge() {
      try {
        this.working = true;
        const { ChallengeName, Session } = this.challenge;
        const { emailAddress } = this.form;

        const parameters = [];
        switch (ChallengeName) {

          case 'NEW_PASSWORD_REQUIRED': {
            const { password, passwordConfirm } = this.form;
            this.$assert(password && password === passwordConfirm, this.$t('auth.errors.passwords-do-not-match'), {
              code: 'PASSWORDS_MISMATCH',
            });

            parameters.push({ key: 'NEW_PASSWORD', value: toDataURI('text/plain;charset=utf8', password) });
            break;
          }

          default:
            this.$assert.fail(new Error(`Unknown challenge ${ChallengeName}`));
        }

        const { errors } = await this.$apollo.mutate({
          mutation: AuthRespondToChallengeMutation,
          variables: {
            challenge: {
              emailAddress,
              challenge: ChallengeName,
              parameters,
              session: Session,
              contextData: this.getCognitoContextData(emailAddress),
            },
          },
          update: (client, { data: { respondToCognitoChallenge } }) => this.updateAuthResponse(client, respondToCognitoChallenge),
          errorPolicy: 'all',
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$log.error(err));

          const err = errors.shift();
          if (err.extensions && err.extensions.ChallengeName) {
            this.prepareChallenge(err.extensions);
          } else {
            throw err;
          }
        } else {
          this.$assert(this.isLoggedIn, new Error('Failed to login by email'));
          await this.afterAuthenticated(this.nextRoute);
        }
      } catch (err) {
        err.user_message = this.$t('general.errored-try-later');
        this.err = err;
        this.working = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Auth/Login.vue